import { isGraphqlConfigured, graphqlRequest } from './graphqlClient';

export { isGraphqlConfigured as isProfileGraphqlConfigured };

export interface UpdateProfileInput {
  displayName?: string;
  focus?: string;
  timezone?: string;
  locale?: string;
  preferences?: Record<string, unknown>;
}

export const updateProfile = async (input: UpdateProfileInput): Promise<void> => {
  await graphqlRequest<unknown>(`
    mutation UpdateProfile($input: UpdateProfileInput!) {
      updateProfile(input: $input) { id }
    }
  `, { input });
};

export interface RemoteProfile {
  /** Account-truth display name (what updateProfile writes); null when never set. */
  displayName: string | null;
  focus: string;
  /** Free-form prefs blob; carries `nameIsCustom`, the displayName provenance flag. */
  preferences: Record<string, unknown> | null;
}

// Reads the signed-in user's profile from the backend so device-spanning fields
// (the greeting `displayName`, the daily intention `focus`) can be hydrated on
// login. The backend `me` returns MePayload { user, profile }: displayName lives
// on the User record (what updateProfile writes), focus on the profile — a
// brand-new user can have the former without the latter, so a missing profile
// still returns the name with an empty focus. Returns null only when there is
// no user record at all.
export const getProfile = async (): Promise<RemoteProfile | null> => {
  const data = await graphqlRequest<{
    me: {
      user: { displayName: string | null } | null;
      profile: { focus: string; preferences: Record<string, unknown> | null } | null;
    } | null;
  }>(`
    query {
      me {
        user { displayName }
        profile { focus preferences }
      }
    }
  `);
  const me = data?.me;
  if (!me) return null;
  return {
    displayName: me.user?.displayName ?? null,
    focus: me.profile?.focus ?? '',
    preferences: me.profile?.preferences ?? null,
  };
};

const profileGraphqlApi = { isProfileGraphqlConfigured: isGraphqlConfigured, updateProfile, getProfile };
export default profileGraphqlApi;
